import React, { useEffect, useState } from 'react';
import styled from "styled-components";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import {  Row, Col } from 'reactstrap';
import SelectField from './FormGroup/SelectField';
import DateField from './FormGroup/DateField'; 
import Button from './Button';
import { useNavigate  } from 'react-router-dom';
import { getDateString } from './Functions';
import moment from 'moment';
import { calendarConfig, handoverDays, maxBookingPeriodDays } from './Constants';
import { OptionsType } from 'Components/Types';
import { VehicleAPI } from 'API/VehicleAPI';
import { BankHolidaysAPI } from 'API/CalendarAPI';

interface IFormInputs {
    vehicle_id: number;
    start_date: Date;
    end_date: Date;
}

const Container = styled.div`
    background-color: #FFF;
    border-radius: 10px;
    padding: 0.5em;
    box-shadow: rgb(38, 57, 77) 0px 20px 30px -10px;
`;

const FormWrapper = styled.form`
    padding:0.5em;
    padding-bottom:0em;
`;

function AvailabilityForm() {
    const [dates, setDates] = useState<Date[]>(new Array(0));
    const [options, setOptions] = useState<OptionsType[]>([]);
    const [vehicleName, setVehicleName] = useState<string>("");
    const [bankHolidays, setBankHolidays] = useState<string[]>([]);

    const methods = useForm<IFormInputs>();
    const navigate = useNavigate();
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);

    useEffect(() => {
        const getOptions = async () => {
            const response = await VehicleAPI.GetMotorhomes();
            const motorhomesList = response.map((option: any) => {
                return { value: option["vehicle_id"], desc: option["name"]};
            });
            setOptions(motorhomesList);
        }
        getOptions();
    },[])

    const onSubmit: SubmitHandler<IFormInputs> = data => {
        const start_date = getDateString(new Date(data.start_date));
        const end_date = getDateString(new Date(data.end_date));

        navigate(`/CheckAvailability/${data.vehicle_id}/${vehicleName.replaceAll(" ", "")}?start=${start_date}&end=${end_date}`);    
    }

    const dateTooltip = () => {
        return (
            <p>Motorhomes can only be collected/returned on Mondays or Fridays. If any day is a bank holiday, the next available weekday becomes selectable.</p>
        );
    }

    const vehicleIdChanged = (e:any) => {
        setVehicleName(e.target.options[e.target.selectedIndex].text);
    }

    useEffect(() => {
        const loadBankHolidays = async () => {
            const bhDates = await BankHolidaysAPI.get();
            setBankHolidays(bhDates);
        };
        loadBankHolidays();
    },[]);

    return (
        <Container>
            <FormProvider {...methods}>
                <FormWrapper onSubmit={methods.handleSubmit(onSubmit)}>
                    <Row>
                        <Col md={4}>
                            <SelectField
                                name="vehicle_id"
                                label="Motorhome"
                                className="motorhome-select"
                                required={true}
                                options={options}
                                onChange={vehicleIdChanged}
                            />
                        </Col>
                        <Col md={3}>
                            <DateField
                                name="start_date"
                                label="Date start"
                                className="motorhome-date"
                                required={true}
                                selectsStart={true}
                                dates={dates}
                                datesUpdater={setDates}
                                minDate={moment().add(1, 'days').toDate()}
                                maxDate={moment().add(calendarConfig.maxSearchableMnthsRng, "months").toDate()}
                                daysAllowed={handoverDays}
                                bankHolidays={bankHolidays}
                                tooltipBody={dateTooltip}
                            />
                        </Col>
                        <Col md={3}>
                            <DateField
                                name="end_date"
                                label="Date end"
                                className="motorhome-date"
                                required={true}
                                selectsEnd={true}
                                dates={dates}
                                datesUpdater={setDates}
                                minDate={dates[0]}
                                maxDate={moment(dates[0]).add(maxBookingPeriodDays, "days").toDate()}
                                daysAllowed={handoverDays}
                                bankHolidays={bankHolidays}
                                tooltipBody={dateTooltip}
                            />
                        </Col>
                        <Col md={2}>
                            <Button
                                type="submit"
                                colour="dark"
                                text="search"
                                css={{marginTop: "4em"}}
                            />
                        </Col>
                    </Row>
                </FormWrapper>
            </FormProvider>
        </Container>
    );
}
export default AvailabilityForm;
